/**
 * Demo presentation modes for the isolated demo deployments.
 *
 * Controlled by `VITE_DEMO_MODE` (alias `VITE_DEMO_NAV_MODE`). This NEVER
 * deletes routes — every route in App.tsx still exists. It only filters which
 * nav items and Switchboard workflows are *visible*, so a demo deployment can
 * present a focused story without a separate build.
 *
 *   VITE_DEMO_MODE=lead-command-center   → focused inbound-lead workflow demo
 *   VITE_DEMO_MODE=agency-admin          → broader (trimmed) agency admin demo
 *   (unset)                              → full portal, no filtering
 */

export type DemoMode = 'lead-command-center' | 'agency-admin';

const RAW =
  (import.meta.env.VITE_DEMO_MODE as string | undefined) ??
  (import.meta.env.VITE_DEMO_NAV_MODE as string | undefined) ??
  '';

function normalize(raw: string): DemoMode | undefined {
  const v = raw.trim().toLowerCase();
  if (v === 'lead' || v === 'leads' || v === 'lead-command-center') return 'lead-command-center';
  if (v === 'agency' || v === 'agency-admin' || v === 'admin') return 'agency-admin';
  return undefined;
}

export const DEMO_MODE: DemoMode | undefined = normalize(RAW);

export function isDemoMode(): boolean {
  return DEMO_MODE !== undefined;
}

export const DEMO_LABELS: Record<DemoMode, string> = {
  'lead-command-center': 'Lead Command Center',
  'agency-admin': 'Agency Admin Command Center',
};

export function demoLabel(): string | undefined {
  return DEMO_MODE ? DEMO_LABELS[DEMO_MODE] : undefined;
}

/**
 * Nav item ids (from admin-oneup portal.config.navigation, plus injected ids
 * below) that are visible in each demo mode. Anything not listed is hidden.
 */
const NAV_ALLOWLIST: Record<DemoMode, string[]> = {
  // Supabase-native Lead Command Center: nav is workflow-based (Command
  // Center → Records → Insights → Knowledge). Almost everything is an
  // injected item (see DEMO_INJECTED_NAV) so the demo controls grouping;
  // only md-vault reuses the portal config entry as-is. Everything is served
  // by the dedicated lead-command-center-demo-api Worker from Supabase.
  'lead-command-center': [
    'lead-dashboard', // injected (see DEMO_INJECTED_NAV)
    'lead-loop', // injected — unified new-leads + follow-ups + approvals feed
    'lcc-leads', // injected (Records → full lead list)
    'lcc-organizations', // injected
    'lcc-contacts', // injected
    'lcc-submissions', // injected
    'lead-reports', // injected
    'lcc-activity', // injected (Insights grouping)
    'md-vault', // Supabase-backed vault docs (Knowledge group from config)
    // NOTE: 'switchboard' omitted for now — its workflows seed Alice, and the
    // LCC demo Worker intentionally ships without the OpenAI copilot routes.
  ],
  'agency-admin': [
    'command-center-today',
    'leads',
    'organizations',
    'tasks',
    'websites',
    'quick-notes', // Communications
    'activity',
    'switchboard',
    'md-vault',
  ],
};

export function demoNavAllows(id: string): boolean {
  if (!DEMO_MODE) return true;
  return NAV_ALLOWLIST[DEMO_MODE].includes(id);
}

/**
 * Nav items injected only in a given demo mode. The route already exists in
 * App.tsx — we only surface it in the sidebar for the relevant demo.
 */
export interface InjectedNavItem {
  id: string;
  label: string;
  route: string;
  icon: string;
  group: string;
  description?: string;
}

export const DEMO_INJECTED_NAV: Record<DemoMode, InjectedNavItem[]> = {
  'lead-command-center': [
    // Command Center — "what needs my attention?"
    {
      id: 'lead-dashboard',
      label: 'Dashboard',
      route: '/demo/lead-command-center',
      icon: 'LayoutDashboard',
      group: 'Command Center',
      description: 'Awareness overview: due now, overdue, approvals, unverified',
    },
    {
      id: 'lead-loop',
      label: 'LeadLoop',
      route: '/demo/lcc/loop',
      icon: 'Repeat',
      group: 'Command Center',
      description: 'One feed of everyone who needs contact next — work it and nothing slips',
    },
    // Records — the CRM objects behind the workflow
    {
      id: 'lcc-leads',
      label: 'Leads',
      route: '/demo/lcc/leads',
      icon: 'UserPlus',
      group: 'Records',
      description: 'Every lead across the whole pipeline',
    },
    {
      id: 'lcc-organizations',
      label: 'Organizations',
      route: '/demo/lcc/organizations',
      icon: 'Building2',
      group: 'Records',
      description: 'Businesses with contacts and lead history',
    },
    {
      id: 'lcc-contacts',
      label: 'Contacts',
      route: '/demo/lcc/contacts',
      icon: 'Users',
      group: 'Records',
      description: 'People linked to organizations and leads',
    },
    {
      id: 'lcc-submissions',
      label: 'Form Submissions',
      route: '/demo/lcc/submissions',
      icon: 'FileInput',
      group: 'Records',
      description: 'Raw intake payloads behind form/ad/chat leads',
    },
    // Insights
    {
      id: 'lead-reports',
      label: 'Reports',
      route: '/demo/lcc/reports',
      icon: 'BarChart3',
      group: 'Insights',
      description: 'Pipeline funnel, sources, and outcomes',
    },
    {
      id: 'lcc-activity',
      label: 'Activity',
      route: '/activity',
      icon: 'Activity',
      group: 'Insights',
      description: 'Recent log entries across the demo',
    },
  ],
  'agency-admin': [],
};

export function demoInjectedNav(): InjectedNavItem[] {
  return DEMO_MODE ? DEMO_INJECTED_NAV[DEMO_MODE] : [];
}

/** Switchboard workflow keys visible per demo mode. */
const SWITCHBOARD_ALLOWLIST: Record<DemoMode, string[]> = {
  'lead-command-center': [
    'lead.add_lead',
    'lead.callback_prep',
    'client.project_summary',
    'website.build_intake',
  ],
  'agency-admin': [
    'lead.add_lead',
    'lead.callback_prep',
    'client.project_summary',
    'website.build_intake',
    'client.prep_call',
  ],
};

export function demoSwitchboardAllows(workflowKey: string): boolean {
  if (!DEMO_MODE) return true;
  return SWITCHBOARD_ALLOWLIST[DEMO_MODE].includes(workflowKey);
}

/** Where the root path ("/") should land for the active demo mode. */
export function demoHomeRoute(): string {
  if (DEMO_MODE === 'lead-command-center') return '/demo/lead-command-center';
  return '/command-center';
}
